import { useGSAP } from "@gsap/react";
import { Box, Button, Paper } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import gsap from "gsap";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import authServiceApi, { fileToBase64 } from "../../api/authService";
import jobPostingApi from "../../api/jobPosting";
import subscriptionApi from "../../api/subscription";
import RocketLauncherLoader from "../../components/common/RocketLauncherLoader";
import RenderStepContent from "../../components/recruiter/RenderStepContent";
import { BASE_URL } from "../../config";
import useJobPostingChoices from "../../hooks/useJobPostingChoices";
import useOfflineJobWizard from "../../hooks/useOfflineJobWizard";
import { useAuthStore } from "../../store/authStore.js";
import { steps } from "../../utils/constants";
import CustomStepper from "./components/CustomStepper";
import useSubscription from "../../hooks/useSubscription.js";
import Swal from "sweetalert2";

const PostJobWizard = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [jobId, setJobId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // removed direct document.getElementById call here; we'll access DOM safely in useGSAP
  const [opacity, setOpacity] = useState(0);
  const { data: jobPostingChoices, isLoading: isLoadingJobPostingChoices } =
    useJobPostingChoices();
  const { data: subscriptionData, isLoading: susbcriptionDataIsLoading } =
    useSubscription();

  const { data: recruiterProfile, isLoading: isLoadingRecruiterProfile } =
    useQuery({
      queryKey: ["recruiter-profile"],
      queryFn: async () => {
        const res = await authServiceApi.getRecruiterProfile();
        return res.data;
      },
    });
  const hasVerifiedRecruiterProfile =
    recruiterProfile?.company_verification_status === "verified";
  const hasActiveSubscription = subscriptionData?.has_subscription;

  useEffect(() => {
    if (!user) return navigate("/signin");
    if (!hasVerifiedRecruiterProfile) {
      Swal.fire({
        title: "Company Profile Not Verified",
        text: "Your company profile needs to be verified before you can post jobs. Please complete your profile and submit it for verification.",
        icon: "warning",
        confirmButtonText: "Go to Profile",
        cancelButtonText: "Not now",
        showCancelButton: true,
        allowOutsideClick: false, // ← add
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/company-profile");
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          // Action if user clicked "Cancel"
          console.log("Cancelled by user.");
        } else {
          navigate(-1);
        }
      });
    } else if (!hasActiveSubscription) {
      Swal.fire({
        title: "Ready to Post a New Job?",
        text: "Click confirm to start creating a new job posting. An active subscription and a verified company profile are required to post jobs.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, let's go!",
        cancelButtonText: "Not now",
        allowOutsideClick: false, // ← add
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/pricing");
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          // Action if user clicked "Cancel"
          // console.log("Cancelled by user.");
        } else {
          navigate(-1);
        }
      });
    }
  }, [user]);

  const initStateData = {
    // Job overview
    title: "",
    location: "",
    job_type: "",
    // department: "",
    pilot_type: "",
    aviation_category: "",
    cover_image: "",
    // posting_channel: "",
    no_of_positions: 1,
    job_reference_code: "",
    confidential_posting: false,
    is_remote: false,

    // Description
    description: "",
    responsibilities: "",
    minimum_qualifications: "",
    benefits: "",
    internal_notes: "",
    // aircraft_type: "",

    // Qualifications & Experience
    experience_level: "",
    aircraft_type: [],
    // license_requirements: "",
    required_license_types: [],
    // required_licenses: [],
    endorsements_required: [],
    total_flying_hours_required: "",
    recency_of_experience_months: "",
    specific_aircraft_hours_required: "",
    minimum_flight_hours: "",
    minimum_pic_hours: "",
    medical_class: "",
    medical_certification_required: "",
    visa_requirements: "local",
    background_check_required: false,
    probation_period_months: "",

    // Screening questions
    // screening_questions: [],

    // Application details
    contact_email: "",
    application_url: "",
    interview_mode: "",
    candidate_communication_type: "",
    salary_min: "",
    salary_max: "",
    expected_start_date: "",
    expiry_date: "",
    status: "active",
    visibility: "public",
    is_urgent: false,
    preferred_candidate_nationalities: "",
  };
  const [formData, setFormData] = useState(initStateData);
  const [animateIn, setAnimateIn] = useState(false);

  const {
    savedState,
    isRestoring,
    saveProgress,
    clearProgress,
    isSaving,
    lastSavedAt,
  } = useOfflineJobWizard(user?.id);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isEditMode = params.get("edit");

    if (isEditMode) return;

    if (!isRestoring) {
      if (savedState) {
        setFormData((prev) => ({ ...initStateData, ...savedState.formData }));
        setActiveStep(savedState.activeStep || 0);
      } else {
        setFormData(initStateData);
        setActiveStep(0);
      }
    }
  }, [isRestoring, savedState, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get("edit");
    const isEditMode = !!editId;

    if (!isEditMode && !isRestoring && user?.id) {
      saveProgress({
        activeStep,
        formData,
      });
    }
  }, [
    activeStep,
    formData,
    saveProgress,
    isRestoring,
    location.search,
    user?.id,
  ]);

  const initializeState = (data = {}) => {
    const mergedData = { ...initStateData, ...data };

    mergedData.cover_image = {
      file: null,
      previewUrl: mergedData.cover_image
        ? BASE_URL + mergedData.cover_image
        : null,
    };

    mergedData.expected_start_date = mergedData.expected_start_date
      ? mergedData.expected_start_date.split("T")[0]
      : "";
    mergedData.expiry_date = mergedData.expiry_date
      ? mergedData.expiry_date.split("T")[0]
      : "";

    return mergedData;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get("edit");
    if (editId) {
      setJobId(editId);
      const fetchJobData = async () => {
        setIsLoading(true);
        try {
          const { data } = await jobPostingApi.getJobPostingById(editId);
          setFormData(initializeState(data));
        } catch (error) {
          toast.error("Failed to load job data for editing.");
        } finally {
          setIsLoading(false);
        }
      };
      fetchJobData();
    } else {
      setIsLoading(true);
      setJobId(null);
      setFormData(initializeState());
      setIsLoading(false);
    }
  }, [location.search, user]);

  useGSAP(() => {
    const stepHeader = document.getElementById("step-header");
    if (!stepHeader) return setOpacity(1);
    const stepLabels = stepHeader.querySelectorAll(".MuiStepLabel-root");

    const tl = gsap.timeline();

    tl.fromTo(
      stepHeader,
      { opacity: 0, y: -40 },
      { opacity: 1, y: 0, duration: 0.7, ease: "power2.out" },
    ).fromTo(
      stepLabels,
      {
        opacity: 0,
        scale: 0.7,
        y: 30,
        rotationX: 90,
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        rotationX: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: "back.out(1.8)",
      },
      "-=0.4",
    );
  }, [isLoading, isSubmitting]);

  const validateStep = (stepIndex) => {
    const newErrors = {};

    switch (stepIndex) {
      case 0: // Job Overview
        if (!formData.title) newErrors.title = "Job title is required";
        if (!formData.location) newErrors.location = "Location is required";
        if (!formData.job_type) newErrors.job_type = "Job Type is required";
        if (!formData.pilot_type)
          newErrors.pilot_type = "Pilot Type is required";
        break;

      case 1: // Description
        if (!formData.description)
          newErrors.description = "Job description is required";
        if (!formData.responsibilities)
          newErrors.responsibilities = "Responsibilities are required";
        break;

      case 2: // Qualifications
        if (!formData.experience_level)
          newErrors.experience_level = "Experience level is required";

        if (!formData.aircraft_type || formData.aircraft_type.length === 0)
          newErrors.aircraft_type = "Aircraft types is required";

        if (
          formData.total_flying_hours_required &&
          formData.minimum_flight_hours &&
          parseInt(formData.minimum_flight_hours) >
            parseInt(formData.total_flying_hours_required)
        ) {
          newErrors.minimum_flight_hours =
            "Minimum flight hours cannot be greater than maximum flying hours.";
        }
        break;

      case 3: // Application Details
        if (!formData.contact_email) {
          newErrors.contact_email = "Email is required";
        }
        if (
          formData.contact_email &&
          !/\S+@\S+\.\S+/.test(formData.contact_email)
        ) {
          newErrors.contact_email = "Email is invalid.";
        }
        if (
          formData.salary_min &&
          formData.salary_max &&
          parseFloat(formData.salary_min) > parseFloat(formData.salary_max)
        ) {
          newErrors.salary_min =
            "Minimum salary cannot be greater than maximum.";
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step navigation
  const handleNext = () => {
    if (!validateStep(activeStep)) return; // stop if invalid

    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Handle form updates
  const handleChange = (field, value) => {
    // console.log(field, value);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  // Format payload before submit
  const formatPayload = () => {
    const payload = { ...formData };
    const numericalFields = [
      "salary_min",
      "salary_max",
      "total_flying_hours_required",
      "specific_aircraft_hours_required",
      "no_of_positions",
      "minimum_flight_hours",
      "minimum_pic_hours",
      "recency_of_experience_months",
      "probation_period_months",
    ];

    numericalFields.forEach((field) => {
      if (payload[field] === "" || payload[field] === null) {
        delete payload[field];
      } else {
        payload[field] = Number(payload[field]);
      }
    });

    ["expected_start_date", "expiry_date"].forEach((field) => {
      if (!payload[field]) {
        payload[field] = null;
      }
    });

    // Arrays check
    payload.required_license_types = Array.isArray(
      payload.required_license_types,
    )
      ? payload.required_license_types
      : [];
    payload.endorsements_required = Array.isArray(payload.endorsements_required)
      ? payload.endorsements_required
      : [];

    // Screening questions validation
    payload.screening_questions = Array.isArray(payload.screening_questions)
      ? payload.screening_questions.map((q) => {
          const cleaned = { ...q };

          // remove empty options for multiple_choice
          if (cleaned.type === "multiple_choice") {
            cleaned.options = (cleaned.options || []).filter((o) => o);
          }

          // remove null/empty values
          Object.keys(cleaned).forEach((key) => {
            if (
              cleaned[key] === "" ||
              cleaned[key] === null ||
              cleaned[key] === undefined
            ) {
              delete cleaned[key];
            }
          });

          return cleaned;
        })
      : [];

    // Remove empty strings
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "" || payload[key] === null) {
        delete payload[key];
      }
    });

    return payload;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const payload = formatPayload();
      if (
        payload?.cover_image instanceof File ||
        payload?.cover_image?.file instanceof File
      ) {
        payload.cover_image = await fileToBase64(payload.cover_image.file);
      } else if (
        typeof payload?.cover_image === "string" ||
        typeof payload?.cover_image?.file === "string"
      ) {
        delete payload.cover_image;
      } else if (!payload?.cover_image || !payload?.cover_image?.file) {
        delete payload.cover_image;
      }

      const isDraft = payload.status === "draft";
      if (jobId) {
        const updateResponse = await jobPostingApi.updateJobPosting(
          jobId,
          payload,
        );
        if (updateResponse.status === 200) {
          try {
            await clearProgress();
          } catch (err) {
            console.error("Failed to clear offline job wizard data:", err);
          }
          toast.success(
            isDraft
              ? "Draft updated successfully!"
              : "Job updated successfully!",
          );
          setIsSubmitting(false);
          navigate("/manage-jobs");
          // setTimeout(() => {
          // }, 2000);
        }
      } else {
        const createResponse = await jobPostingApi.createJobPosting(payload);
        if (createResponse.status === 200 || createResponse.status === 201) {
          try {
            await clearProgress();
          } catch (err) {
            console.error("Failed to clear offline job wizard data:", err);
          }

          toast.success(
            isDraft ? "Draft saved successfully!" : "Job saved successfully!",
          );
          setIsSubmitting(false);
          navigate("/manage-jobs");
          // setTimeout(() => {
          // }, 2000);
        }
      }
    } catch (error) {
      console.error(error);
      const apiErrors = error.response?.data;
      if (apiErrors) {
        setErrors(apiErrors);
        // setActiveStep(steps.length - 1);
      }
      toast.error("An error occurred. Please check the form.");
      setIsSubmitting(false);
    }
  };

  if (isLoading || susbcriptionDataIsLoading || !formData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  if (isLoadingJobPostingChoices) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // if (
  //   !susbcriptionDataIsLoading &&
  //   !hasActiveSubscription &&
  //   !isLoadingRecruiterProfile &&
  //   !hasVerifiedRecruiterProfile
  // ) {
  //   return <Navigate to="/pricing" />;
  // }

  if (isSubmitting) {
    return <RocketLauncherLoader />;
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 3, m: 3 }}>
      {/* <AutosaveStatus isSaving={isSaving} lastSavedAt={lastSavedAt} /> */}
      <div id="step-header" className="" style={{ opacity: opacity }}>
        <div className="text-center mb-4">
          <h2 className="text-lg font-bold text-secondary-800">
            {jobId ? `Edit Job Posting` : `Create New Job Post`}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {jobId
              ? `Updating existing job`
              : `Step ${activeStep + 1} of ${steps.length}`}
          </p>
        </div>

        <CustomStepper activeStep={activeStep} steps={steps} />
      </div>

      <Box>
        <RenderStepContent
          activeStep={activeStep}
          errors={errors}
          formData={formData}
          handleChange={handleChange}
          jobPostingChoices={jobPostingChoices}
          jobId={jobId}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
        }}
      >
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>
        <Box>
          {!jobId && activeStep === steps.length - 1 && (
            <Button
              variant="outlined"
              color="secondary"
              sx={{ mr: 2 }}
              onClick={() => {
                setFormData((prev) => ({ ...prev, status: "draft" }));
                handleSubmit();
              }}
            >
              Save as Draft
            </Button>
          )}
          <Button variant="contained" color="primary" onClick={handleNext}>
            {activeStep === steps.length - 1
              ? jobId
                ? "Update"
                : "Submit"
              : "Next"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default PostJobWizard;
